import { useMutation, useQuery } from '@tanstack/vue-query';
import { AssistanceType } from '~/composables/types/AssistanceType';
import { SavePersonalDetails } from '~/composables/types/PersonalDetails';
import { defineStore } from 'pinia';
import { getFoodAssistanceTypeId, globalFetchOptions } from '~/utils/helpers';

export const useAssistanceTypeStore = defineStore('assistance_type', () => {
  const getAllForSelect = (isAll = false) => {
    const fetcher = async () =>
      $fetch<{ success: boolean; data: AssistanceType[]; count: number }>(`/funds/assistance-types/${isAll}`, {
        ...globalFetchOptions(),
        method: 'get',
      });

    return useQuery({ queryKey: ['assistance-type-for-select'], queryFn: fetcher, ...globalUseQueryOptions() });
  };

  const getFetchAllForSelect = (isAll = false) => {
    return $fetch<{ success: boolean; data: AssistanceType[]; count: number }>(`/funds/assistance-types/${isAll}`, {
      ...globalFetchOptions(),
      method: 'get',
    });
  };

  const getEnabledSA = (saId: string) => {
    return $fetch<{ success: boolean; data: AssistanceType[]; count: number }>(
      `/service-areas/${saId}/enabled-assistance-types`,
      {
        ...globalFetchOptions(),
        method: 'get',
      }
    );
  };

  const getAtByLicenseLocation = () => {
    return $fetch<{ success: boolean; data: AssistanceType[]; count: number }>(`/assistance-types/licensed-locations`, {
      ...globalFetchOptions(),
      method: 'get',
    });
  };

  const enableAt = () => {
    return useMutation((id: string) =>
      $fetch<{ success: boolean; data: { id: string; name: string }[] }>(`/assistance-types/${id}/licensed-locations`, {
        ...globalFetchOptions(),
        method: 'PUT',
        body: {},
        retry: false,
      })
    );
  };

  const enableField = () => {
    return useMutation((data: { id: string; data: any }) =>
      $fetch<SavePersonalDetails>(`/assistance-types/${data.id}/licensed-locations/field`, {
        ...globalFetchOptions(),
        method: 'PUT',
        body: data.data,
        retry: false,
      })
    );
  };

  const getByLicenseLocation = () => {
    return $fetch<SavePersonalDetails>(`/assistance-types`, {
      ...globalFetchOptions(),
      method: 'GET',
      retry: false,
    });
  };

  const saveDescription = (id: string) => {
    return useMutation((data: any) =>
      $fetch<SavePersonalDetails>(`/assistance-types/${id}/licensed-locations/details`, {
        ...globalFetchOptions(),
        method: 'PUT',
        retry: false,
        body: data,
      })
    );
  };

  const saveVideoLink = (id: string) => {
    return useMutation(
      (data: {
        videoLink: string | null;
        videoActive: boolean;
        videoCaption: string | null;
        videoIntroduction: string | null;
      }) =>
        $fetch<{ success: boolean; data: string }>(`/assistance-types/${id}/licensed-locations/video-link`, {
          ...globalFetchOptions(),
          method: 'PUT',
          retry: false,
          body: data,
        })
    );
  };

  const deleteVideoLink = (id: string) => {
    return useMutation(() =>
      $fetch<{ success: boolean; data: string }>(`/assistance-types/${id}/licensed-locations/video-link`, {
        ...globalFetchOptions(),
        method: 'DELETE',
        retry: false,
      })
    );
  };

  const get = async (id: string) => {
    return $fetch<{ success: boolean; data: any }>(`/assistance-types/${id}/configuration`, {
      ...globalFetchOptions(),
      method: 'get',
    });
  };

  const save = (id: string) => {
    return useMutation((formData: any) =>
      $fetch<{ success: boolean; data: any }>(`/assistance-types/configuration/non-distributable/${id}`, {
        ...globalFetchOptions(),
        method: 'POST',
        body: formData,
      })
    );
  };
  const financialCheck = (id: string) => {
    return useMutation(() =>
      $fetch<{ success: boolean; data: any }>(`/assistance-types/financial-warnings/${id}`, {
        ...globalFetchOptions(),
        method: 'GET',
      })
    );
  };
  const editServiceArea = (id: string) => {
    return useMutation((formData: { serviceAreaId: string; method: HTTPMethod }) =>
      $fetch<{ success: boolean; data: ServiceArea }>(
        `/assistance-types/${id}/configuration/service-areas/${formData.serviceAreaId}`,
        {
          ...globalFetchOptions(),
          method: formData.method,
        }
      )
    );
  };
  const getHistory = (assistanceTypeId: string, configurationId: string) => {
    return $fetch<{ success: boolean; data: any }>(
      `/assistance-types/${assistanceTypeId}/configuration/${configurationId}/history`,
      {
        ...globalFetchOptions(),
        method: 'get',
      }
    );
  };

  const getComments = (configurationId: string) => {
    return $fetch<{ success: boolean; data: unknown }>(`/assistance-types/configuration/${configurationId}/comments`, {
      ...globalFetchOptions(),
      method: 'get',
    });
  };

  const addComment = (configurationId: string) => {
    return useMutation((formData: { message: string }) => {
      return $fetch<SavePersonalDetails>(`/assistance-types/configuration/${configurationId}/comments`, {
        ...globalFetchOptions(),
        method: 'POST',
        body: formData,
        retry: false,
      });
    });
  };
  const refetchComments = ref(false);
  const setRefetchComments = () => {
    refetchComments.value = !refetchComments.value;
  };
  return {
    getAllForSelect,
    getFetchAllForSelect,
    getAtByLicenseLocation,
    enableAt,
    enableField,
    getByLicenseLocation,
    saveDescription,
    saveVideoLink,
    deleteVideoLink,
    getEnabledSA,
    get,
    save,
    editServiceArea,
    getHistory,
    getComments,
    addComment,
    refetchComments,
    setRefetchComments,
    financialCheck,
  };
});
